import { ProjectService } from '@services/project.service';
import { Component } from '@angular/core';
import { NavParams, ModalController } from '@ionic/angular';
import { EnumAPIStatus } from '@api/AbstractAPI';
import { EnumGenericRoleActionAlerts } from '@pages/BasePage';
import { EnumMerchantStatus, StatusModel } from '@models/Merchant';
import { EnumActionsPermissions } from '@models/EnumPermissions';

type MygonAssociationMode = 'marketplace' | 'crud';

@Component({
    selector: 'app-mygon-association',
    templateUrl: './mygon-association.component.html',
    styleUrls: ['./mygon-association.component.scss'],
})
export class MygonAssociationModalComponent {

    public Mode: MygonAssociationMode = 'marketplace';

    // ─── Marketplace mode ────────────────────────────────────────────────────
    public MerchantName: string = '';
    public Matches: any[] = [];
    public SelectedMatch: any = null;
    public ConfirmMode: boolean = false;
    public ExpandedMatchId: any = null;
    public ItemData: any = null;
    public IsSynchronized: boolean = false;
    public SourceData: any = null;
    public MerchantInfo: any = null;
    private onUpdateMerchantSync: Function | null = null;

    // ─── CRUD mode ───────────────────────────────────────────────────────────
    public Data: any[] = [];
    public CurrentAssociation: any | null = null;
    public SelectedItem: any | null = null;
    public TermsAccepted: boolean = false;
    public Marketplace: any;
    public MerchantStatusId: any = null;
    private onAssociate: Function | null = null;
    private onDisassociate: Function | null = null;

    // ─── Shared ──────────────────────────────────────────────────────────────
    public PreviewImage: string | null = null;
    public DisplayMerchant: any = null;

    public EnumMerchantStatus = EnumMerchantStatus;
    public EnumActionsPermissions = EnumActionsPermissions;

    constructor(
        private NavParams: NavParams,
        private ModalController: ModalController,
        private ProjectService: ProjectService
    ) {
        this.Mode = this.NavParams.get('mode') || 'marketplace';

        // Props comuns aos 2 modos — alimentam o card rico "Comerciante Marketplace"
        this.MerchantName = this.NavParams.get('merchantName');
        this.SourceData = this.NavParams.get('sourceData') || null;
        this.MerchantInfo = this.NavParams.get('merchantInfo') || null;

        if (this.Mode === 'crud') this.InitCrud();
        else this.InitMarketplace();

        this.DisplayMerchant = (this.MerchantInfo || this.SourceData)
            ? this.BuildDisplayData(this.SourceData, {
                Name: this.MerchantName,
                VatNumber: this.MerchantInfo?.VatNumber,
                Url: this.MerchantInfo?.Url,
                Address: this.MerchantInfo?.Address,
                ZipCode: this.MerchantInfo?.ZipCode
            })
            : null;
    }

    private InitMarketplace(): void {
        this.Matches = (this.NavParams.get('matches') || [])
            .map((m: any) => ({ ...m, Status: new StatusModel(m?.Status) }));
        this.SelectedMatch = this.NavParams.get('selectedMatch') || null;
        this.ConfirmMode = this.NavParams.get('confirmMode') || false;
        this.ItemData = this.NavParams.get('itemData') || null;
        this.IsSynchronized = this.NavParams.get('isSynchronized') ?? false;
        this.onUpdateMerchantSync = this.NavParams.get('onUpdateMerchantSync') || null;
        this.onDisassociate = this.NavParams.get('onDisassociate') || null;

        if (this.ConfirmMode && this.FilteredMatches.length === 1) {
            this.SelectedMatch = this.FilteredMatches[0];
            this.ExpandedMatchId = this.FilteredMatches[0]?.Id;
        }
    }

    private InitCrud(): void {
        this.Data = this.NavParams.get('data') || [];
        this.CurrentAssociation = this.NavParams.get('currentAssociation') || null;
        this.SelectedItem = this.CurrentAssociation;
        this.onAssociate = this.NavParams.get('onAssociate') || null;
        this.onDisassociate = this.NavParams.get('onDisassociate') || null;
        this.onUpdateMerchantSync = this.NavParams.get('onUpdateMerchantSync') || null;
        this.MerchantStatusId = this.NavParams.get('merchantStatusId') ?? null;

        this.TermsAccepted = !!this.CurrentAssociation;
        this.Marketplace = this.ProjectService.GetSettings()?.Configuration?.MarketPlace;
    }

    /**
     * Normaliza shapes diferentes (âncora marketplace aninhada em Location/Contact,
     * shops/matches planos ou aninhados em Data) num objeto plano para o card rico.
     */
    private BuildDisplayData(raw: any, extra: any = null): any {
        if (!raw && !extra) return null;
        const data = raw?.Data ?? raw ?? {};
        const loc = data?.Location ?? raw?.Location ?? {};
        const contact = data?.Contact ?? raw?.Contact ?? {};
        const source = data?.Sources?.[0] ?? raw?.Sources?.[0] ?? {};
        return {
            Images: data?.Images ?? raw?.Images ?? source?.Images ?? [],
            Name: extra?.Name ?? raw?.Name ?? data?.Name ?? null,
            VatNumber: extra?.VatNumber ?? raw?.VatNumber ?? data?.VatNumber ?? null,
            Url: extra?.Url ?? raw?.Url ?? data?.Url ?? source?.Url ?? null,
            Address: loc?.Address ?? data?.Address ?? raw?.Address ?? extra?.Address ?? null,
            Locality: loc?.Locality ?? null,
            ZipCode: loc?.ZipCode ?? data?.ZipCode ?? raw?.ZipCode ?? extra?.ZipCode ?? null,
            District: loc?.District ?? null,
            Phone: contact?.Phone ?? data?.Phone ?? raw?.Phone ?? null,
            MobilePhone: contact?.MobilePhone ?? data?.MobilePhone ?? raw?.MobilePhone ?? null,
            Email: contact?.Email ?? data?.Email ?? raw?.Email ?? null,
            Website: data?.Website ?? contact?.WebSite ?? contact?.Website ?? raw?.Website ?? null,
            Description: data?.Description ?? raw?.Description ?? null,
        };
    }

    // ─── Marketplace mode methods ─────────────────────────────────────────────
    public get FilteredMatches(): any[] {
        return this.Matches?.filter(m => !!m?.Id) ?? [];
    }

    ToggleSelect(match: any, ev?: Event) {
        ev?.stopPropagation();
        this.SelectedMatch = this.SelectedMatch?.Id === match?.Id ? null : match;
    }

    ToggleExpand(match: any) {
        this.ExpandedMatchId = this.ExpandedMatchId === match?.Id ? null : match?.Id;
    }

    Confirm() {
        this.ModalController.dismiss({ match: this.SelectedMatch }, EnumGenericRoleActionAlerts.Confirm);
    }

    ConfirmCreate() {
        this.ModalController.dismiss({ match: { Id: null } }, EnumGenericRoleActionAlerts.Confirm);
    }

    async ForceUpdateMerchantSync() {
        if (!this.onUpdateMerchantSync) return;

        const result = await this.onUpdateMerchantSync();

        if (result?.Status === EnumAPIStatus.Success)
            this.ModalController.dismiss(null, EnumGenericRoleActionAlerts.Close);
    }

    public async OnRemoveMatchAssociation(): Promise<void> {
        if (!this.onDisassociate) return;

        const result = await this.onDisassociate(this.SelectedMatch);
        if (result?.Status === EnumAPIStatus.Success)
            this.ModalController.dismiss(null, EnumGenericRoleActionAlerts.Close);
    }

    // ─── CRUD mode methods ────────────────────────────────────────────────────
    public get IsAssociated(): boolean {
        return !!this.CurrentAssociation;
    }

    public get CanAssociate(): boolean {
        return !!this.SelectedItem && this.TermsAccepted;
    }

    public SelectItem(item: any): void {
        if (this.IsAssociated) return;
        this.SelectedItem = this.SelectedItem?.IdExternal === item?.IdExternal ? null : item;
    }

    public async OnAssociate(): Promise<void> {
        if (!this.CanAssociate || !this.onAssociate) return;
        const result = await this.onAssociate(this.SelectedItem);
        if (result?.Status === EnumAPIStatus.Success)
            this.ModalController.dismiss(null, EnumGenericRoleActionAlerts.Confirm);
    }

    public async OnDisassociate(): Promise<void> {
        if (!this.onDisassociate) return;
        const result = await this.onDisassociate(this.CurrentAssociation);
        if (result?.Status === EnumAPIStatus.Success)
            this.ModalController.dismiss(null, EnumGenericRoleActionAlerts.Confirm);
    }

    // ─── Shared ───────────────────────────────────────────────────────────────
    Close() {
        this.ModalController.dismiss(null, EnumGenericRoleActionAlerts.Close);
    }

    OpenPreview(img: string, ev: Event) {
        ev.stopPropagation();
        this.PreviewImage = img;
    }

    ClosePreview() {
        this.PreviewImage = null;
    }
}
